import {
  Background,
  ReactFlow,
  useReactFlow,
  Handle,
  useNodesData,
  Position,
  getSmoothStepPath,
  BaseEdge,
  EdgeLabelRenderer,
  useNodesState,
  useEdgesState,
  SmoothStepEdge,
} from "@xyflow/react";
import { Badge } from "V2.0/common/badge";
import { Button } from "V2.0/common/button";

import useLayout from "./useLayout";
import { useState, useEffect, useMemo } from "react";
import Drawer from "V2.0/components/Drawer";
import { XMarkIcon } from "@heroicons/react/24/outline";
// import { pipelineRun } from "./mocks/pipelineRun";
import { Tooltip } from "antd";
import clsx from "clsx";
import { NODE_STATUS_COLORS } from "./index2";
import { useConversationById } from "V2.0/HomePages/Threads/components/conversation_messages/index.jsx";
import {
  ConversationMessagesView,
  RichTextMessages,
} from "../../Threads/components/conversation_messages";
import TabsWithBadge from "../../../common/tabsWithBadge";
// import { Icons } from "./nodes/CustomNode";

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat("default", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZoneName: "short",
  }).format(date);
};

function generateNodesAndEdges(pipelineRun) {
  const { nodeExecutions, edges, currentNodeId } = pipelineRun;

  const transformedNodes = nodeExecutions.map((node, idx) => {
    const { nodeId, ...restOfTheNode } = node;
    return {
      id: nodeId,
      data: {
        ...restOfTheNode,
      isCurrentNode: nodeId === currentNodeId
      },
      type: "custom",
      position: { x: 0, y: 0 },
    };
  });

  const transformedEdges = edges.map((edge) => {
    const { from, to, condition } = edge;
    const [status, value] = condition.split("=");

    return {
      id: `${from}->${to}`,
      source: from,
      target: to,
      type: "custom",
      data: {
        [status]: value,
      },
    };
  });

  return {
    transformedNodes,
    transformedEdges,
  };
}

function generateWorkflowRunNodesAndEdges(nodes, workflowRun) {
  const nodeExecutions = workflowRun.nodeExecutions;
  const nodesMap = nodes.reduce((acc, node) => {
    return {
      ...acc,
      [node.nodeId]: node,
    };
  }, {});

  let edges = [];
  for (let i = 0; i < nodeExecutions.length - 1; ++i) {
    const sourceNode = nodeExecutions[i];
    const targetNode = nodeExecutions[i + 1];

    edges.push({
      id: `${sourceNode.nodeId}->${targetNode.nodeId}`,
      source: sourceNode.nodeId,
      target: targetNode.nodeId,
      data: {
        status: sourceNode.status,
      },
      type: "custom",
    });
  }

  let workflowRunNodes = [];
  for (let i = 0; i < nodeExecutions.length; ++i) {
    const executedNode = nodeExecutions[i];
    const node = nodesMap[executedNode.nodeId];
    workflowRunNodes.push({
      id: node.nodeId,
      ...node,
      ...executedNode,
      position: { x: 0, y: 200 * (1 + i) },
      type: "custom",
    });
  }

  return { workflowRunNodes, workflowRunEdges: edges };
}

const CustomNodes = ({ id }) => {
  const internalNode = useNodesData(id);
  const { getNode } = useReactFlow();
  const node = getNode(id);
  const { data } = node;

  return (
    <>
      {data?.nodeType !== "trigger" && (
        <Handle type="target" position={Position.Top} />
      )}
      <div
        className={clsx(
          `cursor-pointer w-[300px] h-[80px] bg-white p-4 rounded-lg shadow-md border
              border-2 "border-blue-500"
             font-sans`,
          data?.status === "completed" ? "border-blue-500" : data.isCurrentNode ? "border-green-500" : ""
        )}
      >
        <Tooltip title={data?.settings?.name}>
          <div className="flex gap-x-2 items-center">
            {/* {Icons[data?.nodeType](data?.settings?.endNodeType)} */}
            <span className="text-sm font-medium max-w-[150px] truncate">
              {data?.settings?.name ||
                (data?.nodeType === "trigger"
                  ? "Inbound Trigger"
                  : "unnamed action")}
            </span>
          </div>
        </Tooltip>
        {data?.settings?.description && (
          <Tooltip title={data?.settings?.description} placement="bottom">
            <p className="text-xs text-gray-500 truncate">
              {data?.settings?.description}
            </p>
          </Tooltip>
        )}
      </div>
      {data?.nodeType !== "end" && (
        <Handle type="source" position={Position.Bottom} />
      )}
    </>
  );
};

export function SimpleEdge(props) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  } = props;
  const { getEdge, getNode } = useReactFlow();
  const edge = getEdge(id);
  const startNode = getNode(edge.source);
  const targetNode = getNode(edge.target);
  const data = edge.data;

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge path={edgePath} />
      {/* <EdgeLabelRenderer>
        <div className=" nodrag nopan">
          <div
            className={`absolute pointer-events-auto `}
            style={{
              transform: `translate(-50%, -100%) translate(${labelX}px,${
                labelY + 12
              }px)`,
            }}
          >
            {data?.output ? (
              <Badge>{data?.output}</Badge>
            ) : (
              <Badge>{data?.status}</Badge>
            )}
          </div>
        </div>
      </EdgeLabelRenderer> */}
    </>
  );
}

const nodeTypes = {
  custom: CustomNodes,
};

const edgeTypes = {
  custom: SimpleEdge,
};

function Conversation(props) {
  const { open, conversation } = props;

  return (
    open?.node?.data?.nodeType === "assistant" &&
    conversation && (
      <div className="flex w-full flex-col gap-y-4 justify-between text-sm">
        <div className="p-4 flex flex-col gap-y-4 max-h-[calc(100vh-180px)] overflow-auto">
          <RichTextMessages
            messages={conversation.messages}
            projectId={conversation.workspaceId}
            conversationId={conversation._id}
          />
        </div>
      </div>
    )
  );
}

function Details(props) {
  const { open, setOpen } = props;

  return (
    <div className="flex flex-col m-4 gap-y-8">
      {/* <div className="flex justify-between gap-x-4 items-center min-h-16">
        <p className="font-semibold text-gray-700">Details</p>
        <div onClick={() => setOpen({ isOpen: false })}>
          <XMarkIcon className="size-4" />
        </div>
      </div> */}

      <div className="max-w-sm flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-2 justify-between text-sm">
          <span className="text-gray-400">Name</span>
          <span className="text-gray-500">
            {open?.node?.data?.settings?.name}
          </span>
        </div>
        <div className="flex flex-col gap-y-2 justify-between text-sm">
          <span className="text-gray-400">Status</span>
          <span className="text-gray-500">
            <Badge color={NODE_STATUS_COLORS[open?.node?.data?.status]}>
              {open?.node?.data?.status}
            </Badge>
          </span>
        </div>
      </div>

      <div className="w-full flex flex-col gap-y-4">
        {open?.node?.data?.nodeType === "analysis" && (
          <div className="flex flex-col gap-y-4 justify-between text-sm">
            <p className="font-sm text-semibold text-gray-600 border-b">
              Analysis
            </p>
            <div className="flex flex-col gap-y-2 justify-between text-sm">
              <span className="text-gray-400">Type</span>
              <span className="text-gray-500">
                {open?.node?.data?.settings?.analysisType}
              </span>
            </div>
            <div className="flex flex-col gap-y-2 justify-between text-sm">
              <span className="text-gray-400">Extracted Parameter</span>
              <span className="text-gray-500">
                {open?.node?.data?.settings?.outputVariableName}
              </span>
            </div>
            {open?.node?.data?.status === "completed" && (
              <div className="flex flex-col gap-y-2 justify-between text-sm">
                <span className="text-gray-400">Extracted Value</span>
                <span className="text-gray-500">
                  {open?.node?.data?.result?.metadata?.analysisOutput}
                </span>
              </div>
            )}
          </div>
        )}
        {open?.node?.data?.startTime && (
          <div className="flex flex-col gap-y-2 justify-between text-sm">
            <span className="text-gray-400">Execution Start time</span>
            <span className="text-gray-500">
              {formatDate(open.node.data.startTime)}
            </span>
          </div>
        )}
        {open?.node?.data?.endTime && (
          <div className="flex flex-col gap-y-2 justify-between text-sm">
            <span className="text-gray-400">Execution End time</span>
            <span className="text-gray-500">
              {formatDate(open.node.data.endTime)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const TABS = {
  Conversation: "Conversation",
  Details: "Details",
};

const Tabs = {
  [TABS.Details]: (props) => <Details {...props} />,
  [TABS.Conversation]: (props) => <Conversation {...props} />,
};

export function RunPreviewer(props) {
  const { workflow, workflowRun } = props;

  // const { nodes, edges } = workflow;
  // const { workflowRunNodes, workflowRunEdges } =
  //   generateWorkflowRunNodesAndEdges(nodes, pipelineRun);
  const { transformedNodes, transformedEdges } =
    generateNodesAndEdges(workflowRun);
  const [open, setOpen] = useState({
    isOpen: false,
  });

  const [nodes, , onNodesChange] = useNodesState(transformedNodes);
  const [edges, , onEdgesChange] = useEdgesState(transformedEdges);
  const { conversation, fetchConversationById } = useConversationById(null);
  const [activeTab, setActiveTab] = useState(TABS.Details);

  useLayout();

  useEffect(() => {
    if (workflowRun) {
      const { workspace, conversation } = workflowRun;
      fetchConversationById(workspace, conversation);
    }
  }, [workflowRun]);

  const tabs = useMemo(() => {
    return Object.values(TABS).map((tab) => {
      return { name: tab, href: "#", current: tab === activeTab };
    });
  }, [activeTab]);

  return (
    <div className="w-full h-full flex">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        onNodeClick={(e, node) => {
          setOpen({
            isOpen: true,
            node,
          });
          if (node?.data?.nodeType === "assistant") {
            setActiveTab(TABS.Conversation);
          } else {
            setActiveTab(TABS.Details);
          }
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
      </ReactFlow>

      {open.isOpen && (
        <aside className=" p-2 w-2/4 h-[100%] bg-white border-l">
          <div className="flex justify-between">
            {open?.node?.data?.nodeType === "assistant" &&
              open?.node?.data?.status != "pending" && (
                <TabsWithBadge
                  tabs={tabs}
                  onTabChange={(e, { tab }) => {
                    setActiveTab(tab.name);
                  }}
                />
              )}
            <div className="w-full self-end">
              <Button plain onClick={() => setOpen({ isOpen: false })}>
                <XMarkIcon className="size-4" />
              </Button>
            </div>
          </div>
          {Tabs[activeTab]({
            open,
            setOpen,
            conversation,
          })}
        </aside>
      )}
    </div>
  );
}
